import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Overline, MaskLine, Reveal, EASE } from "@/components/Motion";

const CHAPTERS = [
  {
    n: "01",
    title: "The Spark",
    text: "It began with a simple question over a plate of pasta and a cup of masala chai — why should anyone have to choose between the romance of Italy and the warmth of India? Forco Amore was imagined as the answer: one table where both belong.",
    img: "/images/real7-mural.jpeg",
    alt: "The hand-painted Forco Amore mural",
  },
  {
    n: "02",
    title: "Two Kitchens, One Dream",
    text: "We brought together cooks raised on nonna-style sauces and chefs who learned dal at their mother's stove. They argued, they tasted, they laughed — and slowly a menu emerged where Alfredo sits proudly beside Dal Makhani.",
    img: "/images/real2.jpeg",
    alt: "Copper handis of slow-cooked curries",
  },
  {
    n: "03",
    title: "Flame, First Language",
    text: "The wood-fired oven and the tandoor were installed side by side — our two hearths. One blisters hand-stretched pizza bases, the other perfumes breads and kebabs with smoke. Everything else in the kitchen answers to them.",
    img: "/images/real9-dining.jpeg",
    alt: "The lantern-lit dining room at Forco Amore",
  },
  {
    n: "04",
    title: "The Bakery Ovens",
    text: "Then came the in-house bakery — tea cakes, pastries, hand-crafted cookies and celebration cakes, baked fresh every morning. The smell of dates-and-almond loaf at 7 AM is now the official start of our day.",
    img: "https://images.unsplash.com/photo-1578985545062-69928b1d9587?q=85&w=1000&auto=format&fit=crop",
    alt: "Chocolate truffle cake from our bakery",
  },
  {
    n: "05",
    title: "The Catering Trail",
    text: "Word travelled. Boardrooms asked for our lunches, families asked for our live counters, weddings asked for the whole kitchen. Today our copper handis and fusion menus travel across Gurugram and beyond — amore, delivered.",
    img: "/images/real4.jpeg",
    alt: "The Forco Amore buffet line at a catered event",
  },
  {
    n: "06",
    title: "Forco Amore, Today",
    text: "You'll find us at DLF Corporate Greens, Sector 74A — espresso machine hissing, tandoor glowing, mural watching over it all. Six days a week, Monday to Saturday, we set the table and wait for the best part: you.",
    img: "/images/real6-storefront.jpeg",
    alt: "The Forco Amore storefront at DLF Corporate Greens",
  },
];

export default function StoryPage() {
  return (
    <main className="pt-[72px]" data-testid="story-page">
      <section className="bg-wine text-cream py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <Overline className="text-brass">Our Story</Overline>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 max-w-4xl" data-testid="story-page-title">
            <MaskLine>Two Culinary Hearts,</MaskLine>
            <MaskLine delay={0.15}>
              <span className="italic text-brass">One Love Story</span>
            </MaskLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
            className="mt-6 max-w-xl text-cream/80 text-base sm:text-lg font-light leading-relaxed"
          >
            The journey of Forco Amore — from a spark of an idea to Gurugram's warmest fusion table.
          </motion.p>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-12 relative">
          <div className="absolute left-6 lg:left-1/2 top-0 bottom-0 w-px bg-sand lg:-translate-x-px" aria-hidden="true" />

          <div className="space-y-20 sm:space-y-28">
            {CHAPTERS.map((c, i) => {
              const flip = i % 2 === 1;
              return (
                <div key={c.n} className="relative grid gap-8 lg:grid-cols-2 lg:gap-20 items-center" data-testid={`story-chapter-${c.n}`}>
                  <span className="absolute left-6 lg:left-1/2 -translate-x-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-wine text-cream font-serif italic text-sm z-10 shadow-[0_10px_25px_-8px_rgba(114,47,55,0.5)]">
                    {c.n}
                  </span>

                  <Reveal className={`pl-16 lg:pl-0 ${flip ? "lg:order-2 lg:pl-16" : "lg:pr-16 lg:text-right"}`}>
                    <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                      {c.title.split(",")[0]}
                      {c.title.includes(",") && <span className="italic text-wine">,{c.title.split(",")[1]}</span>}
                    </h2>
                    <p className="mt-5 text-faded text-base font-light leading-relaxed max-w-lg lg:inline-block">
                      {c.text}
                    </p>
                  </Reveal>

                  <Reveal delay={0.15} className={`pl-16 lg:pl-0 ${flip ? "lg:order-1 lg:pr-16" : "lg:pl-16"}`}>
                    <div className="overflow-hidden rounded-[1.75rem] group">
                      <img
                        src={c.img}
                        alt={c.alt}
                        loading="lazy"
                        className="img-lux w-full aspect-[4/3] object-cover"
                        data-testid={`story-chapter-img-${c.n}`}
                      />
                    </div>
                  </Reveal>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-shell border-t border-sand py-20 sm:py-24">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <Overline className="text-wine">The Next Chapter</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight mt-5">
              Is Written <span className="italic text-wine">With You</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/reservations"
                data-testid="story-reserve-cta"
                className="rounded-full bg-wine px-10 py-4 text-sm tracking-[0.16em] uppercase text-cream hover:bg-winedark hover:scale-[1.03] active:scale-95 transition-all duration-300"
              >
                Reserve a Table
              </Link>
              <Link
                to="/menu"
                data-testid="story-menu-cta"
                className="rounded-full border border-wine px-10 py-4 text-sm tracking-[0.16em] uppercase text-wine hover:bg-wine hover:text-cream transition-colors duration-300"
              >
                See the Menu
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
